import { RocheUIStoryTeaser, RocheUIStoryType } from '@repo/roche-ui';
import React, { createRef, RefObject, useEffect, useRef, useState } from 'react';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { useTranslation } from '../../hooks/use-translation';
import { LanguageService } from '../../services/language';
import { StoryItem, StoryItems, StoryTeaserService } from '../../services/story-teaser';
import { StringService } from '../../services/string';
import { parseAssetSource } from '../../utils/asset';
import { trackStoryTeaserClick } from './roche-story-teaser';
import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date/index';
import { getAltText } from './alt-text-helper';

interface RocheStoryTeaserAutoState {
  teasers: StoryItems;
}

const initialState: RocheStoryTeaserAutoState = {
  teasers: {} as StoryItems,
};

export const RocheStoryTeaserAuto = ({ blok, updateHoistedState, slot, wrapperTag }: any): JSX.Element => {
  const [allTeasers, setAllTeasers] = useState<RocheStoryTeaserAutoState>(initialState);
  const [formattedDate, setFormattedDate] = React.useState<string[] | undefined>(undefined);

  const Wrapper = wrapperTag as React.ElementType;

  // translations
  const translations = {
    readStory: useTranslation('read the story'),
    [RocheUIStoryType.PODCAST]: useTranslation('podcast'),
    [RocheUIStoryType.SERIES]: useTranslation('series'),
    [RocheUIStoryType.SLIDESHOW]: useTranslation('slideshow'),
    [RocheUIStoryType.VIDEO]: useTranslation('video'),
  };

  // tracking
  const storyTeaserRefs = useRef<RefObject<HTMLDivElement>[]>([]);

  const getTeaserTagLabel = (teaser: StoryItem): string | undefined => {
    if (
      teaser.content?.teaser_type &&
      teaser.content?.teaser_type !== '' &&
      teaser.content?.teaser_type !== RocheUIStoryType.DEFAULT
    ) {
      return StringService.capitalize(translations[teaser.content?.teaser_type as RocheUIStoryType]);
    }
    return undefined;
  };

  const getTeaserType = (teaser: StoryItem): RocheUIStoryType | undefined => {
    if (teaser.content?.teaser_type && teaser.content?.teaser_type !== '') {
      return teaser.content?.teaser_type as RocheUIStoryType;
    }
    return undefined;
  };

  useEffect(() => {
    const { group_tags: groupTags, page, sorting, tags, teaser_number: number } = blok;

    if (!sorting || !number || [...tags, ...groupTags].length <= 0) {
      return;
    }

    const formatDate = async (teasers: StoryItems) => {
      if (!teasers.items) return;

      const dates = await Promise.all(
        teasers.items.map(async (teaser: StoryItem) => {
          return await DateService.formatDate(teaser.first_published_at, 'dateFull');
        }),
      );
      setFormattedDate(dates);
    };

    StoryTeaserService.getStoryTeasers(tags, groupTags, sorting, page || 1, number).then((result) => {
      setAllTeasers({ teasers: result });
      storyTeaserRefs.current = result.items.map((_, i) => storyTeaserRefs.current[i] ?? createRef());
      formatDate(result);
      return updateHoistedState?.(result.items?.length, result.total, `${tags}${groupTags}`, result);
    });
  }, []);

  if (Object.keys(allTeasers.teasers).length <= 0) {
    return null;
  }

  return (
    <>
      {allTeasers.teasers.items.map((teaser: StoryItem, index: number) => (
        <ConditionalWrapper
          key={index}
          condition={wrapperTag !== undefined && wrapperTag !== ''}
          wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
        >
          <div key={index} slot={slot} ref={storyTeaserRefs.current[index]}>
            <RocheUIStoryTeaser
              layout={blok.layout}
              teaserImage={parseAssetSource(teaser.content?.teaser_image[0]?.src.filename)}
              teaserImageAlt={getAltText(teaser.content?.teaser_image[0])}
              teaserTitle={teaser.content?.teaser_title}
              teaserType={getTeaserType(teaser)}
              teaserDescription={teaser.content?.teaser_description}
              teaserDate={formattedDate ? formattedDate[index] : undefined}
              dateTimeValue={DateService.getDateTimeValue(teaser?.first_published_at)}
              teaserUrl={LanguageService.getLocaleAwareLink(
                teaser?.default_full_slug,
                LanguageService.getParsedLocale(LanguageService.getActiveLanguage()),
              )}
              teaserTagLabel={getTeaserTagLabel(teaser)}
              ctaLabel={StringService.capitalize(translations.readStory)}
              onTeaserLinkClick={() => trackStoryTeaserClick(teaser, storyTeaserRefs.current[index])}
            />
          </div>
        </ConditionalWrapper>
      ))}
    </>
  );
};
